import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ForcedPasswordChange = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const { changePassword, currentUser } = useAuth();
  const navigate = useNavigate();

  // Validation functions
  const validatePassword = (password) => {
    const errors = {};
    
    // Different validation rules based on user role
    if (currentUser.role === 'lmsuser') {
      // Relaxed validation for lmsuser role
      if (password.length < 5) {
        errors.length = 'Password must be at least 5 characters';
      }
    } else {
      // Strict validation for all other roles
      if (password.length < 8) {
        errors.length = 'Password must be at least 8 characters';
      }
      
      if (!/(?=.*[a-z])/.test(password)) {
        errors.lowercase = 'Password must contain at least one lowercase letter';
      }
      
      if (!/(?=.*[A-Z])/.test(password)) {
        errors.uppercase = 'Password must contain at least one uppercase letter';
      }
      
      if (!/(?=.*\d)/.test(password)) {
        errors.number = 'Password must contain at least one number';
      }
      
      if (!/^[a-zA-Z\d@$!%*?&]+$/.test(password)) {
        errors.specialChars = 'Password can only contain letters, numbers, and special characters (@$!%*?&)';
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationErrors({});
    
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    // Validate password
    const passwordErrors = validatePassword(newPassword);
    if (Object.keys(passwordErrors).length > 0) {
      setValidationErrors(passwordErrors);
      return;
    }
    
    try {
      setLoading(true);
      await changePassword(currentPassword, newPassword);
      // Password change was successful, navigate to home
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="max-w-md w-full bg-slate-800 rounded-lg shadow-lg p-8 border border-slate-700">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-200">Password Change Required</h2>
          <p className="text-slate-400 mt-2">
            For security reasons, you must change your password before continuing.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-800 rounded text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="currentPassword" className="block text-slate-300 mb-1">
              Current Password
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="newPassword" className="block text-slate-300 mb-1">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-slate-300 mb-1">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              required
            />
          </div>

          {/* Password requirements */}
          {newPassword && (
            <div className="mb-6 p-3 bg-slate-700/50 border border-slate-600 rounded">
              <h3 className="text-slate-300 mb-2 font-medium">Password Requirements:</h3>
              <ul className="space-y-1 text-sm">
                {currentUser.role === 'lmsuser' ? (
                  <li className={`flex items-center ${newPassword.length >= 5 ? 'text-green-400' : 'text-slate-400'}`}>
                    <span className="mr-2">{newPassword.length >= 5 ? '✓' : '○'}</span>
                    At least 5 characters
                  </li>
                ) : (
                  <>
                    <li className={`flex items-center ${newPassword.length >= 8 ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="mr-2">{newPassword.length >= 8 ? '✓' : '○'}</span>
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${/(?=.*[a-z])/.test(newPassword) ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="mr-2">{/(?=.*[a-z])/.test(newPassword) ? '✓' : '○'}</span>
                      At least one lowercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*[A-Z])/.test(newPassword) ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="mr-2">{/(?=.*[A-Z])/.test(newPassword) ? '✓' : '○'}</span>
                      At least one uppercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*\d)/.test(newPassword) ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="mr-2">{/(?=.*\d)/.test(newPassword) ? '✓' : '○'}</span>
                      At least one number
                    </li>
                    <li className={`flex items-center ${/^[a-zA-Z\d@$!%*?&]+$/.test(newPassword) ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="mr-2">{/^[a-zA-Z\d@$!%*?&]+$/.test(newPassword) ? '✓' : '○'}</span>
                      Only letters, numbers, and allowed special characters (@$!%*?&)
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}

          {/* Validation errors */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="mb-6 p-3 bg-red-900/50 border border-red-800 rounded text-red-300">
              <ul className="space-y-1 text-sm">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index} className="flex items-center">
                    <span className="mr-2">✗</span>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForcedPasswordChange;